import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  MoreVertical,
  Power,
  PowerOff,
  Unplug,
  RefreshCw,
  ArrowRightLeft
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { PropBotConnectedAccount } from '@/hooks/use-prop-bot-detail-admin';
import { useBotAccountReassignment } from '@/hooks/use-bot-account-reassignment';
import { ReversePositionBadge } from '@/components/common/ReversePositionBadge';
import { PropBot } from '@/types/prop-bots';

interface EnhancedAccountCardProps {
  account: PropBotConnectedAccount;
  isSelected: boolean;
  onSelectionChange: (selected: boolean) => void;
  onEnable: () => void;
  onDisable: () => void;
  onDisconnect: () => void;
  onRefreshBalance: () => void;
  onReassign?: () => void;
  isProcessing: boolean;
  currentBot?: PropBot;
}

export const EnhancedAccountCard: React.FC<EnhancedAccountCardProps> = ({
  account,
  isSelected,
  onSelectionChange,
  onEnable,
  onDisable,
  onDisconnect,
  onRefreshBalance,
  onReassign,
  isProcessing,
  currentBot
}) => {
  const isActive = account.status === 'active';

  return (
    <div className={`p-4 border rounded-lg transition-all duration-200 ${
      isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-border/80'
    }`}>
      <div className="flex items-center justify-between">
        {/* Selection Checkbox */}
        <div className="flex items-center gap-3">
          <Checkbox
            checked={isSelected}
            onCheckedChange={onSelectionChange}
            disabled={isProcessing}
          />
          
          <Avatar className="h-10 w-10">
            <AvatarImage src={account.userAvatar} alt={account.userName} />
            <AvatarFallback className="bg-primary/10 text-primary">
              {account.userName.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          
          <div>
            <div className="font-medium">{account.userName}</div>
            <div className="text-sm text-muted-foreground">{account.userEmail}</div>
            <div className="text-xs text-muted-foreground">
              Account: {account.accountNumber}
            </div>
          </div>
        </div>
        
        {/* Account Details & Actions */}
        <div className="text-right space-y-1">
          <div className="text-lg font-semibold text-green-600">
            ${(account.balance || 0).toLocaleString()}
          </div>
          
          <div className="flex items-center gap-2 justify-end">
            <span className="text-sm">Vol: {account.volumeMultiplier}x</span>
            <ReversePositionBadge isReverse={account.isReversePosition} size="sm" />
          </div>
          
          <div className="flex items-center gap-2 justify-end">
            <Badge 
              variant={isActive ? 'default' : 'secondary'} 
              className={`text-xs ${
                isActive ? 'bg-green-100 text-green-800 border-green-200' : ''
              }`}
            >
              {account.status}
            </Badge>
            <Badge variant={account.isLive ? 'destructive' : 'secondary'} className="text-xs">
              {account.isLive ? 'Live' : 'Demo'}
            </Badge>
          </div>
          
          {(account.broker || account.propFirm) && (
            <div className="text-xs text-muted-foreground">
              {account.broker && `Broker: ${account.broker}`}
              {account.propFirm && ` • Prop: ${account.propFirm}`}
            </div>
          )}
          
          <div className="text-xs text-muted-foreground">
            Added: {formatDistanceToNow(new Date(account.addedDate), { addSuffix: true })}
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-1 justify-end mt-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={onRefreshBalance}
              disabled={isProcessing}
              title="Refresh Balance"
            >
              <RefreshCw className={`h-3 w-3 ${isProcessing ? 'animate-spin' : ''}`} />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={isProcessing}>
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isActive ? (
                  <DropdownMenuItem onClick={onDisable} className="text-orange-600">
                    <PowerOff className="h-4 w-4 mr-2" />
                    Disable Account
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={onEnable} className="text-green-600">
                    <Power className="h-4 w-4 mr-2" />
                    Enable Account
                  </DropdownMenuItem>
                )}
                
                <DropdownMenuItem onClick={onDisconnect} className="text-destructive">
                  <Unplug className="h-4 w-4 mr-2" />
                  Disconnect Account
                </DropdownMenuItem>
                
                {onReassign && (
                  <DropdownMenuItem onClick={onReassign} className="text-blue-600">
                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                    Reassign to Bot
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
};